import { FC, useRef, useState, useEffect, MutableRefObject } from "react";
import type { IndicatorInstance } from "features/indicators/model";
import type { ZoomRange, CandleWithFlag } from "features/chart";
import type { ServerBasePrice } from "services/types";
import { ChevronIcon, PencilIcon } from "shared/ui";
import { IndicatorSettingsModal } from "../indicator-settings-modal/IndicatorSettingsModal";
import { getInputSummary } from "../current-indicators-list/getInputSummary";
import { INDICATORS } from "../config";
import { renderOscillator } from "./renderOscillator";
import styles from "./IndicatorPanel.module.css";

interface Props {
  instance: IndicatorInstance;
  dataRef: MutableRefObject<ServerBasePrice[]>;
  zoomRangeRef: MutableRefObject<ZoomRange>;
  onRegisterDraw: (fn: () => void) => void;
  onUnregisterDraw: () => void;
}

export const IndicatorPanel: FC<Props> = ({
  instance, dataRef, zoomRangeRef, onRegisterDraw, onUnregisterDraw,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const instanceRef = useRef(instance);
  instanceRef.current = instance;

  const drawRef = useRef<() => void>(() => {});
  drawRef.current = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = rect.width * devicePixelRatio;
    canvas.height = rect.height * devicePixelRatio;
    ctx.scale(devicePixelRatio, devicePixelRatio);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    renderOscillator(
      ctx, rect.width, rect.height,
      dataRef.current as CandleWithFlag[],
      zoomRangeRef.current,
      instanceRef.current,
    );
  };

  const onUnregisterRef = useRef(onUnregisterDraw);
  onUnregisterRef.current = onUnregisterDraw;

  useEffect(() => {
    onRegisterDraw(() => drawRef.current());
    return () => onUnregisterRef.current();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = getInputSummary(instance.name, instance.settings);
  const label = INDICATORS.find(i => i.name === instance.name)?.label ?? instance.name;

  return (
    <div ref={containerRef} className={styles.panel}>
      <canvas ref={canvasRef} className={styles.canvas} />
      <div className={styles.label}>
        <button className={styles.collapseBtn} onClick={() => setCollapsed(p => !p)}>
          <ChevronIcon direction={collapsed ? "right" : "down"} fill="white" width={10} height={6} />
        </button>
        {!collapsed && (
          <>
            <span className={styles.labelText}>{label} {summary}</span>
            <button className={styles.editBtn} onClick={() => setShowSettings(true)}>
              <PencilIcon fill="#9CA3AF" width={16} height={16} />
            </button>
          </>
        )}
      </div>
      {showSettings && (
        <IndicatorSettingsModal
          instanceId={instance.id}
          indicatorName={instance.name}
          label={label}
          onClose={() => {
            setShowSettings(false);
            requestAnimationFrame(() => drawRef.current());
          }}
        />
      )}
    </div>
  );
};
